/**
 * The owner's rules (allow | ask | deny) over the few actions that still wait for a validation by default: what reaches
 * other people or cannot be undone on the owner's accounts, tasks on the owner's own computer, answering a mission's
 * question in his place, opening clearly confidential content. Pure code shared by the agent (the gate) and the web app
 * (labels): matching is deterministic, the most specific rule wins and, at equal specificity, deny > ask > allow.
 */
import { ICLOUD_ACTIONS, ICLOUD_SERVICES, knownIcloudSlug } from "./icloud";
import { classifyAppTool, externalTargets, recipientText } from "./risk";
import { siteOf } from "./vault";

export const RULE_ACTIONS = [
  "send_email",
  "reply_email",
  "calendar_invite",
  "payment",
  "delete",
  "public_post",
  "local_computer",
  "sensitive_read",
  "mission_answer",
  "any",
] as const;
export type RuleAction = (typeof RULE_ACTIONS)[number];
/** The action kinds a gated action can have ("any" only appears in rules). */
export type GatedAction = Exclude<RuleAction, "any">;
export const RULE_EFFECTS = ["allow", "ask", "deny"] as const;
export type RuleEffect = (typeof RULE_EFFECTS)[number];
export type RuleOrigin = "message" | "approval" | "app";

/** A rule as stored and shown. Every scope field is optional (null: any). */
export interface RuleView {
  id: string;
  effect: RuleEffect;
  action: RuleAction;
  /** App / toolkit slug in lower case (gmail, googlecalendar, slack…). */
  app: string | null;
  /** Recipient or sender: an e-mail address, a phone number, or a domain (example.com). */
  address: string | null;
  /** A contact's name, matched in the action's recipients and text. */
  contact: string | null;
  /** A word or phrase matched in the action's text (subject, body, title…). */
  keyword: string | null;
  createdFrom: RuleOrigin;
  createdAt: number;
  /** The rule in one French sentence. */
  label: string;
}

export type RuleInput = Pick<RuleView, "effect" | "action"> & Partial<Pick<RuleView, "app" | "address" | "contact" | "keyword">>;

/** What a gated action is, as the rules see it. */
export interface RuleFacts {
  /** null: an action that never waits by default (a read, a reversible change of the owner's own data). */
  action: GatedAction | null;
  app: string | null;
  /** Lower-case recipients (addresses, phones) that are not the owner's own. */
  targets: string[];
  /** Searchable text of the action (arguments, recipients' raw values), compared after folding. */
  text: string;
  /**
   * The recipient side only (addresses, display names): what an allow rule's contact is matched against. Absent: the
   * action has no recipient field, and an allow rule that names a contact does not cover it.
   */
  recipientText?: string;
}

/** Actions an "any" rule never ALLOWS: money, deletions and the owner's computer need a rule that names them. */
const EXPLICIT_ALLOW_ONLY: ReadonlySet<GatedAction> = new Set(["payment", "delete", "local_computer"]);

/** Lower case, no accents, single spaces. */
export function foldText(s: string): string {
  return s
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/** Canonical form of a rule's address: lower case, a leading "@" dropped (domains). */
export function normalizeAddress(raw: string): string {
  return raw.trim().toLowerCase().replace(/^@/, "").replace(/^mailto:/, "");
}

function addressMatches(ruleAddress: string, target: string): boolean {
  const a = normalizeAddress(ruleAddress);
  const t = target.trim().toLowerCase();
  if (!a || !t) return false;
  if (a.includes("@")) return a === t;
  // A phone number: compare digits only.
  if (/^\+?[\d\s().-]{6,}$/.test(a)) return a.replace(/\D/g, "") === t.replace(/\D/g, "") && t.replace(/\D/g, "").length > 0;
  // A domain: the target's domain is it, or one of its subdomains.
  const domain = t.includes("@") ? t.slice(t.lastIndexOf("@") + 1) : t;
  return domain === a || domain.endsWith(`.${a}`);
}

function matches(rule: Pick<RuleView, "effect" | "action" | "app" | "address" | "contact" | "keyword">, facts: RuleFacts, target: string | null): boolean {
  const allow = rule.effect === "allow";
  // A vault fill (a password or a card typed into a page) is allowed only by a rule that names the vault AND the exact
  // site: never by « any », an action-wide rule, a contact or a keyword (the page's address is the attacker's choice).
  if (allow && facts.app === VAULT_RULE_APP && (rule.action === "any" || rule.app?.toLowerCase() !== VAULT_RULE_APP || !rule.address)) return false;
  if (rule.action === "any") {
    // An action that never waits by default is only ever restricted (ask, deny), never "allowed" by a rule.
    if (facts.action === null && rule.effect === "allow") return false;
    if (rule.effect === "allow" && facts.action && EXPLICIT_ALLOW_ONLY.has(facts.action)) return false;
  } else if (rule.action !== facts.action) return false;
  if (rule.app && rule.app.toLowerCase() !== (facts.app ?? "")) return false;
  if (rule.address && (target === null || !addressMatches(rule.address, target))) return false;
  const text = foldText(facts.text);
  // An allow rule's contact is matched on the recipients only (a name in the body proves nothing about who receives
  // it); ask and deny rules only add care and keep matching the whole text.
  if (rule.contact && !(allow ? foldText(facts.recipientText ?? "") : text).includes(foldText(rule.contact))) return false;
  if (rule.keyword && !text.includes(foldText(rule.keyword))) return false;
  // A keyword says what the action is about, not who receives it: alone, it never allows reaching someone.
  if (allow && rule.keyword && !rule.address && !rule.contact && facts.targets.length) return false;
  return true;
}

function specificity(rule: Pick<RuleView, "action" | "app" | "address" | "contact" | "keyword">): number {
  return (rule.action === "any" ? 0 : 1) + [rule.app, rule.address, rule.contact, rule.keyword].filter(Boolean).length;
}

const STRENGTH: Record<RuleEffect, number> = { allow: 0, ask: 1, deny: 2 };

function decideOne<R extends Pick<RuleView, "effect" | "action" | "app" | "address" | "contact" | "keyword">>(rules: readonly R[], facts: RuleFacts, target: string | null): R | null {
  let best: R | null = null;
  for (const r of rules) {
    if (!matches(r, facts, target)) continue;
    if (!best || specificity(r) > specificity(best) || (specificity(r) === specificity(best) && STRENGTH[r.effect] > STRENGTH[best.effect])) best = r;
  }
  return best;
}

/**
 * The rule that decides an action, or null when none applies (the default policy then decides). With several
 * recipients each one is decided on its own: one deny denies, one ask asks, and the action is allowed only when every
 * recipient is.
 */
export function decideByRules<R extends Pick<RuleView, "effect" | "action" | "app" | "address" | "contact" | "keyword">>(rules: readonly R[], facts: RuleFacts): R | null {
  if (!rules.length) return null;
  const targets = facts.targets.length ? facts.targets : [null];
  const picks = targets.map((t) => decideOne(rules, facts, t));
  const strongest = (effect: RuleEffect) => picks.find((p) => p?.effect === effect) ?? null;
  const deny = strongest("deny");
  if (deny) return deny;
  const ask = strongest("ask");
  if (ask) return ask;
  if (picks.every((p) => p?.effect === "allow")) return picks[0]!;
  // Some recipients are allowed, others are not covered: the default decides.
  return null;
}

// ───────────── What an app action is ─────────────

const PAYMENT = /(^|_)(PAY|PAYMENT|PAYOUT|TRANSFER|PURCHASE|BUY|ORDER|CHECKOUT|CHARGE|REFUND|INVOICE_SEND|BOOK|SUBSCRIBE|SUBSCRIPTION|CANCEL_SUBSCRIPTION)(_|$)/;
const DELETE = /(^|_)(DELETE|TRASH|REMOVE|DESTROY|PURGE|WIPE|EMPTY_TRASH)(_|$)/;
const REPLY = /(^|_)(REPLY|REPLY_TO_THREAD)(_|$)/;
const INVITE = /(^|_)(INVITE|RSVP)(_|$)/;
const EVENT_WRITE = /EVENT/;
const PUBLIC = /(^|_)(POST|PUBLISH|TWEET|RETWEET|COMMENT|MAKE_PUBLIC|CREATE_POST)(_|$)/;
const SEND = /(^|_)(SEND|FORWARD|MESSAGE|DM|CHAT|EMAIL|NOTIFY|BROADCAST|SHARE)(_|$)/;

/** The toolkit of a tool slug (GMAIL_SEND_EMAIL → gmail). */
export function toolkitOf(slug: string): string {
  return slug.trim().split("_")[0]!.toLowerCase();
}

/**
 * The rule facts of an app action. A read is a gated action only when the agent marked it confidential
 * (`sensitive`); a reversible change of the owner's own data never is.
 */
export function appRuleFacts(slug: string, args: Record<string, unknown>, ownIdentities: string[] = [], sensitive = false): RuleFacts & { risk: ReturnType<typeof classifyAppTool> } {
  const s = slug.trim().toUpperCase();
  const risk = classifyAppTool(s, args, ownIdentities);
  const verb = s.includes("_") ? s.slice(s.indexOf("_") + 1) : s;
  let action: GatedAction | null = null;
  const icloud = knownIcloudSlug(s);
  if (risk === "read") action = sensitive ? "sensitive_read" : null;
  else if (icloud) {
    // iCloud actions (Pulsez's own executor) follow their explicit table (shared/icloud.ts): an event change that
    // reaches attendees other than the owner is an invitation; the owner's own calendar and mailbox are not gated.
    action = ICLOUD_ACTIONS[icloud].kind ?? (risk === "external" ? "calendar_invite" : risk === "critical" ? "delete" : null);
  } else if (risk === "external" || risk === "critical") {
    if (PAYMENT.test(verb)) action = "payment";
    else if (DELETE.test(verb)) action = "delete";
    else if (REPLY.test(verb)) action = "reply_email";
    else if (INVITE.test(verb) || EVENT_WRITE.test(verb)) action = "calendar_invite";
    else if (SEND.test(verb)) action = "send_email";
    else if (PUBLIC.test(verb)) action = "public_post";
  }
  const targets = [...new Set(externalTargets(args, ownIdentities).map((t) => t.trim().toLowerCase()).filter(Boolean))];
  let text = "";
  try {
    text = `${s} ${JSON.stringify(args)}`;
  } catch {
    text = s;
  }
  return { action, app: toolkitOf(s), targets, text: text.slice(0, 20_000), recipientText: recipientText(args), risk };
}

/** The allow rules « TOUJOURS » creates for an app action (one per recipient), or null when it cannot be scoped. */
export function alwaysRulesForApp(facts: RuleFacts): RuleInput[] | null {
  const a = facts.action;
  if (!a || a === "payment" || a === "delete" || a === "local_computer" || a === "mission_answer") return null;
  if (a === "send_email" || a === "reply_email" || a === "calendar_invite") {
    // Never "every recipient": a send is always scoped to who receives it.
    if (!facts.targets.length || facts.targets.length > 5) return null;
    return facts.targets.map((t) => ({ effect: "allow", action: a, app: facts.app, address: t }));
  }
  return [{ effect: "allow", action: a, app: facts.app }];
}

// ───────────── The vault (« Coffre ») ─────────────

/** The app a vault fill carries in the rules (« sur Coffre »). */
export const VAULT_RULE_APP = "coffre";

/**
 * The rule facts of a vault fill that asks by default (agent/vault/fill.ts): a card or bank item typed into a form
 * pays (« payment »); a login or identity details typed on a site other than the item's own, or an item with no site,
 * would leave for a site the owner never tied them to (« sensitive_read »). Its one target is the page's site
 * (registrable domain), so a rule can name it as its address (« example.com »).
 */
export function vaultRuleFacts(fill: { actionKind: "payment" | "sensitive_read"; origin: string; label?: string; itemKind?: string }): RuleFacts {
  const site = siteOf(fill.origin);
  return { action: fill.actionKind, app: VAULT_RULE_APP, targets: site ? [site] : [], text: [fill.label, fill.itemKind, fill.origin].filter(Boolean).join(" ").slice(0, 2000) };
}

/**
 * The allow rule « TOUJOURS » creates for a vault fill: an identifier or identity on this site, from the vault. Never
 * for a payment (each one is decided on its own, like every payment).
 */
export function alwaysRulesForVault(facts: RuleFacts): RuleInput[] | null {
  if (facts.action !== "sensitive_read" || facts.targets.length !== 1) return null;
  return [{ effect: "allow", action: "sensitive_read", app: VAULT_RULE_APP, address: facts.targets[0]! }];
}

// ───────────── Words ─────────────

export const RULE_ACTION_LABELS: Record<RuleAction, string> = {
  send_email: "envoyer un e-mail ou un message",
  reply_email: "répondre à un e-mail ou un message",
  calendar_invite: "inviter quelqu'un dans ton agenda",
  payment: "payer, acheter ou s'abonner",
  delete: "supprimer tes données dans une app",
  public_post: "publier",
  local_computer: "travailler sur ton ordinateur",
  sensitive_read: "ouvrir un contenu confidentiel",
  mission_answer: "répondre à la question d'une mission à ta place",
  any: "toute action qui demande ton accord",
};

const RULE_ACTION_LABELS_EN: Record<RuleAction, string> = {
  send_email: "send an e-mail or message",
  reply_email: "reply to an e-mail or message",
  calendar_invite: "invite someone in your calendar",
  payment: "pay, buy or subscribe",
  delete: "delete your data in an app",
  public_post: "publish",
  local_computer: "work on your computer",
  sensitive_read: "open confidential content",
  mission_answer: "answer a mission's question for you",
  any: "any action that needs your approval",
};

export const RULE_EFFECT_LABELS: Record<RuleEffect, string> = {
  allow: "Sans demander",
  ask: "Toujours demander",
  deny: "Jamais",
};

const APP_NAMES: Record<string, string> = {
  gmail: "Gmail",
  googlecalendar: "Google Agenda",
  googledrive: "Google Drive",
  googledocs: "Google Docs",
  googlesheets: "Google Sheets",
  outlook: "Outlook",
  slack: "Slack",
  notion: "Notion",
  github: "GitHub",
  linear: "Linear",
  whatsapp: "WhatsApp",
  twitter: "X",
  linkedin: "LinkedIn",
  discord: "Discord",
  stripe: "Stripe",
  [VAULT_RULE_APP]: "Coffre",
  [ICLOUD_SERVICES.mail.toolkit]: ICLOUD_SERVICES.mail.name,
  [ICLOUD_SERVICES.calendar.toolkit]: ICLOUD_SERVICES.calendar.name,
};

export function appName(app: string): string {
  return APP_NAMES[app.toLowerCase()] ?? app.charAt(0).toUpperCase() + app.slice(1);
}

/** The scope of a rule in words ("pour marc@example.com sur Gmail"), "" when it has none. */
export function ruleScopeText(r: Pick<RuleView, "app" | "address" | "contact" | "keyword">, english = false): string {
  const parts: string[] = [];
  if (r.address) parts.push(english ? `for ${r.address}` : `pour ${r.address}`);
  if (r.contact) parts.push(english ? `for ${r.contact}` : `pour ${r.contact}`);
  if (r.keyword) parts.push(english ? `about « ${r.keyword} »` : `quand il s'agit de « ${r.keyword} »`);
  if (r.app) parts.push(english ? `on ${appName(r.app)}` : `sur ${appName(r.app)}`);
  return parts.join(" ");
}

/** A rule in one sentence: « Sans demander : répondre à un e-mail ou un message pour soeur@example.com sur Gmail ». */
export function describeRule(r: Pick<RuleView, "effect" | "action" | "app" | "address" | "contact" | "keyword">): string {
  const scope = ruleScopeText(r);
  return `${RULE_EFFECT_LABELS[r.effect]} : ${RULE_ACTION_LABELS[r.action]}${scope ? ` ${scope}` : ""}`;
}

/** What « TOUJOURS ABCD » will stop asking for, in the user's words (French or English). */
export function alwaysScopeText(rules: readonly RuleInput[], english = false): string {
  if (!rules.length) return "";
  const first = rules[0]!;
  const addresses = rules.map((r) => r.address).filter((a): a is string => Boolean(a));
  const what = english ? RULE_ACTION_LABELS_EN[first.action] : RULE_ACTION_LABELS[first.action];
  const app = first.app ? (english ? ` on ${appName(first.app)}` : ` sur ${appName(first.app)}`) : "";
  const who = addresses.length ? (english ? ` to ${addresses.join(", ")}` : ` à ${addresses.join(", ")}`) : "";
  return `${what}${who}${app}`;
}
